import { submitSubtaskResult, submitResultReview } from 'components/TasksV2/api/results'
import {
  upsertTaskResult,
  mergeFetchedTaskResults,
  ResultStatusRequest,
  resolveResultOccurrence,
  findResultForMutation,
} from 'components/TasksV2/model/resultRequests'
import { AxiosResponse } from 'axios'
import { Content } from 'components/TasksV2/TaskCreation/FormStepContext'
import { httpAuth } from 'config/apiClient'
import endpoints from 'constants/endpoints'
import {
  IConfigGroup,
  ITag,
  Result,
  Results,
  RootState,
  Subtasks,
  Subtask as SubtaskV2,
  Task,
  Reservations,
  TaskInsightsReportPeriod,
} from 'constants/interfaces'
import keys from 'constants/keys'
import { isEmpty, isNumber } from 'lodash'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { extractedUidsFromGroups, extractSubstringAfterDelimiter, getUTCOffset } from 'utils'

export interface TaskCreationBody {
  groups?: string[]
  users?: string[]
  tags?: string[] | null
  from_ts: string
  to_ts: string
  deadline: string
  utc_offset: number
  send_schedule_push: boolean
  send_campaign_start_push: boolean
  data: PollData
  sub_tasks: (PollSubTask | Subtask)[]
}

export interface EditTaskBody {
  tid: string
  utc_offset: number
  updated_to_ts: string
  updated_review_to_ts?: string | null
  updated_deadline: string
  updated_task_users: string[]
  updated_task_groups: string[]
  updated_task_tags: string[]
  task_data: {
    title: string
    view: number
    allowed_overdue_days: number
    contents?: Content[]
    priority: number
  }
}

interface PollData {
  title: string
  is_recurring: boolean
  recurrence_frequency: null | number
  recurrence_days_of_week: number | number[] | null
  categories: string[]
  view: number | null
  type: number
  is_individual_results: boolean
  recurrence_start_time: string | null
  contents?: Content[]
  is_categories_required?: boolean
}
export interface Subtask {
  title: string
  type: number
  external_link?: string
  reference_images?: string[]
}

export interface PollSubTask {
  title: string
  type: number
  is_multi_choice: boolean
  options: {
    [key: string]: string
  }
}

interface Response {
  data_columns: string[]
  data_rows: any[][]
}

export type SubtaskDeepDiveItem = {
  st_id: string
  type: number
  status: number
  context_id: string
  title: string
  is_ai_enhanced?: boolean
  prompt_id?: string
  message_id?: string
  reference_images?: string[]
  content?: {
    images?: string[]
    videos?: string[]
    text?: string
    rich_text?: any
  }
  // reference_items?: string[]
}

export interface TransformedDataWithCounts {
  [context_id: string]: {
    items: SubtaskDeepDiveItem[]
    typeCounts: { [type: number]: number }
  }
}
export type DeepDiveInsights = {
  contexts: {
    [key: string]: {
      status: number
      taskContextId: string
    }
  }
  insights: TransformedDataWithCounts
  /**
   * Which task this deep dive belongs to. The store holds exactly one at a time, and nothing else in
   * the payload identifies the task (`taskContextId` is an audience id), so optimistic patches must
   * check this before writing — the overview modal can be open on a different task than the one whose
   * deep dive is cached.
   */
  tid?: string
}

export type TasksInsights = Record<string, TaskFromInsights>

export type RecurrenceTsTaskInsights = {
  recurrence_deadline: string
  recurrence_ts: string
}
export type TaskInsightsResult = {
  context_id: string
  recurrence_ts: string | null
  st_id: string
  status: number
  rating?: number
  created_at_ts?: number
  rework_count?: number
  // The insights endpoint returns score_deduction at the TOP LEVEL (nullable), not nested.
  score_deduction?: number | null
  reservations?: {
    reasons: string[]
    score_deduction: number
  }
}

export type InsightsSubtaskMeta = { title: string; type?: number; category?: string }

export type TaskFromInsights = {
  title: string
  watchers: string[]
  auditors?: string[]
  priority: number
  desc: string | null
  from_ts: string
  to_ts: string
  review_to_ts?: string
  created_by: string
  view: number
  type: number // 0 = DATA_TASK, 1 = MANUAL_TASK, 2 = TRAINING_TASK, 3 = HISTORY_TASK
  is_recurring: boolean
  recurrence_frequency: number
  recurrence_interval: number
  recurrence_days_of_week: number[] | null
  is_individual_results: boolean
  users: string[]
  groups: string[]
  tags: string[]
  // The backend returns each subtask as an object with title + metadata.
  // Read titles via `getInsightsSubtaskTitle` (handles legacy string shape too).
  sub_tasks: {
    [key: string]: InsightsSubtaskMeta
  }
  deadline: string
  recurrence_ts: RecurrenceTsTaskInsights[]
  recurrence_label: number | null
  results: TaskInsightsResult[]
  is_brief_task?: boolean
}
export type Filter = {
  all?: boolean
  groups?: string[]
  tags?: string[]
  users?: string[]
  createdByMe?: boolean
  myFollowings?: boolean
  myAudits?: boolean
}

export const actionTypes = {
  CREATE_TASK: 'CREATE_TASKS',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_SUBTASKS: 'SET_SUBTASKS',
  SET_TASKS: 'SET_TASKS',
  SET_OWNER_TASKS: 'SET_OWNER_TASKS',
  SET_POLL_RESULTS: 'SET_POLL_RESULTS',
  SET_USER_RESULTS: 'SET_USER_RESULTS',
  SET_ALL_RESULTS: 'SET_ALL_RESULTS',
  SET_TASKS_DEEP_DIVE_INSIGHTS: 'SET_TASKS_DEEP_DIVE_INSIGHTS',
  SET_TASKS_INSIGHTS: 'SET_TASKS_INSIGHTS',
  PATCH_INSIGHTS_RESULT: 'PATCH_INSIGHTS_RESULT',
  PATCH_DEEP_DIVE_RESULT: 'PATCH_DEEP_DIVE_RESULT',
  SET_FULL_TASK_FROM_INSIGHTS: 'SET_FULL_TASK_FROM_INSIGHTS',
  SET_TASK_OVERVIEW_MODAL: 'SET_TASK_OVERVIEW_MODAL',
  PATCH_TASK_OVERVIEW_MODAL: 'PATCH_TASK_OVERVIEW_MODAL',
  SET_IS_INSIGHTS_LOADING: 'SET_IS_INSIGHTS_LOADING',
  SET_TASK_AUDIENCE_MAP: 'SET_TASK_AUDIENCE_MAP',
  SET_FILTER: 'SET_FILTER',
  SET_ALL_RESULTS_LOADING: 'SET_ALL_RESULTS_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
}

export const setFilter = (filter: Filter) => ({
  type: actionTypes.SET_FILTER,
  payload: filter,
})

export const setIsLoading = (isLoading: boolean) => ({
  type: actionTypes.SET_IS_LOADING,
  payload: isLoading,
})

export const setInsightsLoading = (isLoading: boolean) => ({
  type: actionTypes.SET_IS_INSIGHTS_LOADING,
  payload: isLoading,
})

export const setAllResultsLoading = (isLoading: boolean) => ({
  type: actionTypes.SET_ALL_RESULTS_LOADING,
  payload: isLoading,
})

export const setError = (error: { message: string; status?: number } | null) => ({
  type: actionTypes.SET_ERROR,
  payload: error,
})

export const clearError = () => ({
  type: actionTypes.CLEAR_ERROR,
})

export const setSubtasks = (subtasks: Subtasks) => ({
  type: actionTypes.SET_SUBTASKS,
  payload: subtasks,
})

export const setTasks = (tasks: Task[]) => ({
  type: actionTypes.SET_TASKS,
  payload: tasks,
})

export const setOwnerTasks = (ownerTasks: Task[]) => ({
  type: actionTypes.SET_OWNER_TASKS,
  payload: ownerTasks,
})

export const setPollResults = (pollResults: Results) => ({
  type: actionTypes.SET_POLL_RESULTS,
  payload: pollResults,
})

export const setUserResults = (userResults: Results) => ({
  type: actionTypes.SET_USER_RESULTS,
  payload: userResults,
})

export const setAllResults = (allResults: Results) => ({
  type: actionTypes.SET_ALL_RESULTS,
  payload: allResults,
})
export const setOtherUsersResults = (otherResults: Results) => ({
  type: actionTypes.SET_USER_RESULTS,
  payload: otherResults,
})

export const setTasksDeepDiveInsights = (deepDiveInsights: DeepDiveInsights) => ({
  type: actionTypes.SET_TASKS_DEEP_DIVE_INSIGHTS,
  payload: deepDiveInsights,
})

export const setTasksInsights = (insights: TasksInsights, reportPeriod?: TaskInsightsReportPeriod | null) => ({
  type: actionTypes.SET_TASKS_INSIGHTS,
  payload: insights,
  // Undefined preserves the loaded range for local task edits; null explicitly clears it.
  reportPeriod,
})

// Optimistically upsert a single result into insights[tid].results so the dashboard cards
// (which read insights, not allResults) reflect executor/auditor status changes immediately.
export const patchInsightsResult = (tid: string, result: TaskInsightsResult) => ({
  type: actionTypes.PATCH_INSIGHTS_RESULT,
  payload: { tid, result },
})

/**
 * Optimistically patch one subtask row of the deep dive.
 *
 * `patchInsightsResult` alone is NOT enough for the dashboard context rows: `buildContextRows` unions
 * `insights[tid].results` with `deepDiveInsights.insights[contextId].items` and lets the deep-dive rows
 * WIN, because they are normally the fresher source. So for a subtask that already has a row there —
 * exactly the case when an auditor approves or rejects a submitted result — the insights patch is
 * overwritten and the row keeps its old status. Patch both.
 */
export const patchDeepDiveResult = (tid: string, contextId: string, patch: { st_id: string; status: number }) => ({
  type: actionTypes.PATCH_DEEP_DIVE_RESULT,
  payload: { tid, contextId, patch },
})
export const setFullTaskFromInsights = (task: Task) => ({
  type: actionTypes.SET_FULL_TASK_FROM_INSIGHTS,
  payload: task,
})

export const setTaskOverviewModal = (payload: RootState['tasks_v2']['taskOverviewModal']) => ({
  type: actionTypes.SET_TASK_OVERVIEW_MODAL,
  payload,
})

export const patchTaskOverviewModal = (payload: Partial<NonNullable<RootState['tasks_v2']['taskOverviewModal']>>) => ({
  type: actionTypes.PATCH_TASK_OVERVIEW_MODAL,
  payload,
})

export const setTaskAudienceMap = (taskAudienceMap: TasksAudienceMap) => ({
  type: actionTypes.SET_TASK_AUDIENCE_MAP,
  payload: taskAudienceMap,
})

export const pingTask =
  (tid: string): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      await httpAuth.put(endpoints.pingTask, { tid })
      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }

export const createTask =
  (
    data: TaskCreationBody,
    options?: { keepLoading?: boolean }
  ): ThunkAction<Promise<string | null>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(setIsLoading(true))
    try {
      const response = await httpAuth.post(endpoints.tasksV2, data)
      const responseData = response?.data?.success
      return responseData?.tid ?? null
    } catch (e) {
      console.log(e)
      return null
    } finally {
      if (!options?.keepLoading) {
        dispatch(setIsLoading(false))
      }
    }
  }

export const getTasks =
  (): ThunkAction<Promise<Results | null>, RootState, unknown, AnyAction> => async (dispatch, getState) => {
    const { activeGroupID } = getState().config
    const tags: string[] = []
    try {
      dispatch(setIsLoading(true))
      const response = await httpAuth.get(endpoints.tasksV2, {
        params: {
          context_id: activeGroupID,
          utc_offset: getUTCOffset(),
          ...(tags.length > 0 ? { tags } : {}),
        },
      })
      const { data } = response
      if (!isEmpty(data)) {
        dispatch(setSubtasks(data.sub_tasks))
        dispatch(setTasks(data.tasks.filter((task: Task) => task.type !== 1)))
        dispatch(setOwnerTasks(data.owner_tasks ?? []))
        dispatch(setUserResults(data.results))
        dispatch(setAllResults(data.results))
      }

      return data?.results ?? null
    } catch (e) {
      console.log(e)
      return null
    } finally {
      dispatch(setIsLoading(false))
    }
  }

interface UpdatePollResultsBody {
  tid: string
  st_id: string
  status: number
  type: number
  votes?: number[]
  images?: string[]
  videos?: string[]
  audios?: string[]
  files?: string[]
  resultContextId: string
  isExternalLink?: boolean
  isNumeric?: boolean
  answer?: string | number | null
  optimistic?: boolean
  useOptimisticOnly?: boolean
  rating?: number
  reservations?: Reservations
  // Optional recurrence slot of the result — pass it so the optimistic insights patch is tagged
  // with the correct recurrence_ts (state.tasks_v2.tasks is empty on the dashboard, so it can't be
  // derived there). Omit for non-recurring tasks.
  recurrence_ts?: string | null
}
const updateResultsArray = upsertTaskResult

const buildSubtaskDataObject = (
  type: number,
  images?: string[],
  videos?: string[],
  files?: string[],
  votes?: number[],
  answer?: string | number | null,
  isNumeric?: boolean
) => {
  const baseObj = (() => {
    switch (type) {
      case keys.SUBTASK_TYPES.IMAGE_SUBTASK:
        return { images: images || [] }
      case keys.SUBTASK_TYPES.VIDEO_SUBTASKS:
        return { videos: videos || [] }
      case keys.SUBTASK_TYPES.UPLOAD_FILE_SUBTASK:
        return { files: files || [] }
      case keys.SUBTASK_TYPES.POLL_SUBTASK:
        return { poll_selections: votes || [] }
      case keys.SUBTASK_TYPES.PRIVATE_POLL:
        return { poll_selections: votes || [] }
      case keys.SUBTASK_TYPES.OPEN_QUESTION:
        return { answer, is_numeric: isNumeric }
      default:
        return {}
    }
  })()

  return baseObj
}
type calculateContextIDArgs = {
  recipient_type: number
  is_individual_results: boolean
  uid: string
  groupId: string
  tagId: string | null
  tags: ITag[]
  retail_id_context_id: string
  rid: string
  dashboardContextId?: string
}

type calculateContextID = {
  currentTag: ITag
  uid: string
  groupId: string
}
const calculateTagContextId = ({ currentTag, uid, groupId }: calculateContextID) => {
  const tagGroups = currentTag?.groups ?? []
  const tagUsers = currentTag?.users ?? []

  if (tagUsers.includes(uid)) {
    return uid
  }
  if (tagGroups.includes(groupId)) {
    return groupId
  }
  return uid
}
export const calculateResultContextID = ({
  recipient_type,
  is_individual_results,
  uid,
  groupId,
  retail_id_context_id,
  rid,
  tagId,
  tags,
  dashboardContextId,
}: calculateContextIDArgs) => {
  const userId = dashboardContextId || uid
  // if recipient type is user and  is_individual_results = false or individual_results = true
  if (recipient_type === 0 || is_individual_results) {
    return userId //return uid
  }
  // if recipient type is tag
  if (recipient_type === 2) {
    const currentTag = tags.find((tag) => tag.sk === tagId)
    return calculateTagContextId({ currentTag: currentTag!, uid: userId, groupId }) //return uid or groupId
  }
  // if recipient type is group
  return extractSubstringAfterDelimiter(retail_id_context_id, rid) //return groupID
}

interface getTaskAudienceArgs {
  retailTags: ITag[]
  groups: string[]
  users: string[]
  tags: string[]
  is_individual_results: boolean
  groupsObject: { [key: string]: IConfigGroup }
  isVM?: boolean
  permittedGroupIds?: string[]
  permittedUserIds?: string[]
  permittedGroupsByFeature?: { [feature: string]: string[] }
}

const buildPermittedTaskGroupsSet = ({
  isVM,
  permittedGroupIds,
  permittedGroupsByFeature,
}: Pick<getTaskAudienceArgs, 'isVM' | 'permittedGroupIds' | 'permittedGroupsByFeature'>) => {
  const permittedVMGroups = isVM ? permittedGroupsByFeature?.VM ?? [] : []
  const shouldFilterGroups = permittedGroupIds !== undefined || permittedVMGroups.length > 0

  if (!shouldFilterGroups) {
    return null
  }

  return new Set([...(permittedGroupIds ?? []), ...permittedVMGroups])
}

export const getTaskAudience = ({
  retailTags,
  groups,
  users,
  tags,
  is_individual_results,
  groupsObject,
  isVM = false,
  permittedGroupIds,
  permittedUserIds,
  permittedGroupsByFeature,
}: getTaskAudienceArgs) => {
  const permittedGroupsSet = buildPermittedTaskGroupsSet({ isVM, permittedGroupIds, permittedGroupsByFeature })
  const permittedUsersSet =
    permittedUserIds !== undefined || permittedGroupsSet
      ? new Set([
          ...(permittedUserIds ?? []),
          ...extractedUidsFromGroups(Array.from(permittedGroupsSet ?? []), groupsObject).filter(Boolean),
        ])
      : null
  const selectedTagsSet = tags.length > 0 ? new Set(tags) : null
  const groupsSet = new Set<string>()
  const usersSet = new Set<string>()

  const addGroup = (groupId?: string) => {
    if (!groupId) {
      return
    }
    groupsSet.add(groupId)
  }

  groups.forEach(addGroup)
  users.forEach((uid) => usersSet.add(uid))

  if (selectedTagsSet) {
    for (const tag of retailTags) {
      if (!selectedTagsSet.has(tag.sk)) {
        continue
      }

      tag.groups?.forEach(addGroup)
      tag.users?.forEach((uid) => usersSet.add(uid))
    }
  }

  const audienceGroups = permittedGroupsSet
    ? Array.from(groupsSet).filter((groupId) => permittedGroupsSet.has(groupId))
    : Array.from(groupsSet)
  const audienceUsers = permittedUsersSet
    ? Array.from(usersSet).filter((uid) => permittedUsersSet.has(uid))
    : Array.from(usersSet)

  if (!is_individual_results) {
    return { groups: audienceGroups, users: audienceUsers }
  }

  const individualUsersSet = new Set<string>()

  audienceGroups.forEach((groupId) => {
    groupsObject[groupId]?.uids?.forEach((uid) => individualUsersSet.add(uid))
  })
  audienceUsers.forEach((uid) => individualUsersSet.add(uid))

  return { groups: [], users: Array.from(individualUsersSet) }
}

export const updateResults =
  ({
    tid,
    votes = [],
    status,
    images = [],
    videos = [],
    files = [],
    st_id,
    type,
    resultContextId,
    isExternalLink,
    isNumeric,
    answer,
    optimistic = true,
    useOptimisticOnly = false,
    rating,
    reservations,
    recurrence_ts,
  }: UpdatePollResultsBody): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { tasks, taskFromInsights } = getState().tasks_v2
    const { uid } = getState().auth
    const {
      activeGroupID,
      config: { rid },
    } = getState().config
    const { allResults } = getState().tasks_v2
    // The task can live either in the regular `tasks` list (own execution flow) or in
    // `taskFromInsights` (opened from dashboard/insights). Fall back to the latter so
    // fields like retail_id_context_id / recipient_type resolve instead of throwing.
    const currentTask =
      tasks.find((task) => task.tid === tid) ?? (taskFromInsights?.tid === tid ? taskFromInsights : undefined)
    const currentSubtask = getState().tasks_v2.subtasks[tid]?.find((subtask) => subtask.st_id === st_id)

    const occurrence = resolveResultOccurrence(recurrence_ts, currentTask?.recurrence_ts)
    const scopedResult = findResultForMutation(allResults?.[tid], st_id, resultContextId, occurrence)
    const userResultOfCurrentSubtask = scopedResult?.status ? scopedResult : undefined

    const isFirstResult = !userResultOfCurrentSubtask

    const contextIdRecurrenceTs = `${resultContextId}_${occurrence}`
    const updatedCurrentResult = {
      sub_task_type: type,
      st_id,
      status: status,
      context_id: resultContextId,
      context_id_recurrence_ts: contextIdRecurrenceTs,
      ...(type === keys.SUBTASK_TYPES.POLL_SUBTASK || type === keys.SUBTASK_TYPES.PRIVATE_POLL
        ? { is_multi_choice: currentSubtask?.is_multi_choice ?? false }
        : {}),
      updated_by: uid,
      updated_at_ts: Math.floor(Date.now() / 1000),
      created_by: isFirstResult ? uid : userResultOfCurrentSubtask.created_by,
      ...buildSubtaskDataObject(type, images, videos, files, votes, answer, isNumeric),
      ...(rating !== undefined && rating >= keys.RATING_VALUES.POOR && rating <= keys.RATING_VALUES.EXCELLENT
        ? { rating }
        : {}),
      ...(reservations !== undefined ? { reservations } : {}),
    }

    const applyResult = () => {
      const latest = getState().tasks_v2
      dispatch(
        setUserResults({
          ...latest.userResults,
          [tid]: updateResultsArray(latest.userResults?.[tid] || [], updatedCurrentResult),
        })
      )
      dispatch(
        setAllResults({
          ...latest.allResults,
          [tid]: updateResultsArray(latest.allResults?.[tid] || [], updatedCurrentResult),
        })
      )

      // Optimistically mirror into insights so dashboard cards (which read insights, not allResults)
      // reflect the executor's status change immediately. recurrence_ts matches what is sent to the
      // server (null for non-recurring); recurring tasks opened via the dashboard are unaffected here.
      dispatch(
        patchInsightsResult(tid, {
          context_id: resultContextId,
          recurrence_ts: occurrence,
          st_id,
          status,
          // use the filtered rating (0/undefined omitted), matching updatedCurrentResult and the auditor path
          rating: updatedCurrentResult.rating,
          reservations,
          score_deduction: reservations?.score_deduction ?? null,
          created_at_ts: updatedCurrentResult.updated_at_ts,
        })
      )
      // The deep-dive row wins over the insights row in `buildContextRows`, so it has to be patched too.
      dispatch(patchDeepDiveResult(tid, resultContextId, { st_id, status }))
    }
    if (optimistic || isExternalLink || useOptimisticOnly) applyResult()

    if (isExternalLink || useOptimisticOnly) {
      return true
    }

    const data = {
      st_id,
      tid,
      task_context_id: extractSubstringAfterDelimiter(currentTask?.retail_id_context_id!, rid),
      context_id: activeGroupID,
      is_individual_results: currentTask?.is_individual_results,
      recurrence_ts: occurrence,
      recurrence_start_time: currentTask?.is_recurring ? currentTask?.recurrence_start_time : null,
      utc_offset: getUTCOffset(),
      is_first_result: isFirstResult,
      recipient_type: currentTask?.recipient_type,
      data: {
        sub_task_type: type,
        status: status,
        ...(currentSubtask?.type === keys.SUBTASK_V2_TYPES.POLL_SUBTASK ||
        currentSubtask?.type === keys.SUBTASK_TYPES.PRIVATE_POLL
          ? { is_multi_choice: currentSubtask?.is_multi_choice ?? false }
          : {}),
        ...buildSubtaskDataObject(type, images, videos, files, votes, answer, isNumeric),
        ...(rating !== undefined && rating >= keys.RATING_VALUES.POOR && rating <= keys.RATING_VALUES.EXCELLENT
          ? { rating }
          : {}),
        ...(reservations !== undefined ? { reservations } : {}),
      },
    }

    try {
      const response = await submitSubtaskResult(data)
      if (response && !optimistic) applyResult()
      return !!response
    } catch (e) {
      console.log(e)
      return false
    }
  }

export const deleteTask =
  ({ tid }: { tid: string }): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(setIsLoading(true))

    const tasks = getState().tasks_v2.tasks
    const updatedTasks = tasks.filter((task) => task.tid !== tid)
    dispatch(setTasks(updatedTasks))

    const isDeleteAll = true
    const config = {
      data: {
        tid,
        is_delete_all: isDeleteAll,
      },
    }

    try {
      const response = await httpAuth.delete(endpoints.tasksV2, config)
      return !!response
    } catch (e) {
      console.log(e)
      return false
    } finally {
      dispatch(setIsLoading(false))
    }
  }

type GetResultsArgs = {
  subtasksIds: string[]
  task: Task
}
export const getAllResults =
  ({ subtasksIds, task }: GetResultsArgs): ThunkAction<Promise<Results | null>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { tasks_v2, config } = getState()
    const { allResults } = tasks_v2
    const { activeGroupID } = config

    try {
      dispatch(setAllResultsLoading(true))

      if (!task) {
        return null
      }
      const { tid } = task

      const body = {
        recurrence_ts: task.recurrence_ts ?? null,
        sub_tasks_ids: subtasksIds,
        recurrence_interval: task.recurrence_interval ?? null,
        recurrence_days_of_week: task.recurrence_days_of_week ?? null,
        from_ts: task.from_ts ?? null,
        ...(isNumber(task.recurrence_frequency) ? { recurrence_frequency: task.recurrence_frequency } : {}),
      }

      const response: AxiosResponse<any> = await httpAuth.post(endpoints.getAllResults(activeGroupID!), body)
      const { data } = response

      const results: Result[] = subtasksIds.flatMap((id) => data[id] || [])

      const latestResults = getState().tasks_v2.allResults
      const allResultsOfCurrentTask: Results = {
        ...latestResults,
        [tid]: mergeFetchedTaskResults(latestResults[tid] || [], results, allResults[tid] || [], task.recurrence_ts),
      }

      dispatch(setAllResults(allResultsOfCurrentTask))

      return allResultsOfCurrentTask
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setAllResultsLoading(false))
    }
  }

type GetSubtasksResultsOverviewArgs = {
  tid: string
  contextIds: string[]
  sub_tasks_ids: string[]
  recurrence_ts: string | null
  recurrence_frequency: number | null
  recurrence_interval: number | null
  shouldApply?: () => boolean
}

export const getSubtasksResultsOverview =
  ({
    tid,
    contextIds,
    sub_tasks_ids,
    recurrence_ts,
    recurrence_frequency,
    recurrence_interval,
    shouldApply,
  }: GetSubtasksResultsOverviewArgs): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const atRequestStart = getState().tasks_v2.allResults[tid] || []
    dispatch(setIsLoading(true))
    try {
      const body = {
        contexts_ids: contextIds,
        tid: tid,
        recurrence_ts: recurrence_ts,
        recurrence_frequency: recurrence_frequency,
        recurrence_interval: recurrence_interval,
        sub_tasks_ids: sub_tasks_ids ?? [],
      }

      const response = await httpAuth.post(endpoints.getSubtasksResultsOverview, body)
      if (shouldApply && !shouldApply()) return

      const { data } = response
      const { allResults } = getState().tasks_v2

      const results: Result[] = data ?? []

      const allResultsOfCurrentTask: Results = {
        ...allResults,
        [tid]: mergeFetchedTaskResults(allResults[tid] || [], results, atRequestStart, recurrence_ts),
      }

      dispatch(setAllResults(allResultsOfCurrentTask))
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

const transformResponseWithCounts = (response: Response): TransformedDataWithCounts => {
  try {
    const { data_columns, data_rows } = response
    if (!data_columns || !data_rows) {
      return {}
    }

    // Transform the response data into a structure with counts per type
    return data_rows.reduce<TransformedDataWithCounts>((acc, row) => {
      const rowObject = data_columns.reduce<any>((obj, column, index) => {
        obj[column] = row[index]
        return obj
      }, {})

      const contextId: string = rowObject.context_id
      const type: number = rowObject.type

      if (!acc[contextId]) {
        acc[contextId] = { items: [], typeCounts: {} }
      }

      // Push the row object into the items array
      acc[contextId].items.push(rowObject)

      // Count the occurrences of each type
      if (!acc[contextId].typeCounts[type]) {
        acc[contextId].typeCounts[type] = 0
      }
      acc[contextId].typeCounts[type] += 1

      return acc
    }, {})
  } catch (e) {
    console.error(e)
    return {}
  }
}

type CalculateContextIdsOfStatusesArgs = {
  is_individual_results: boolean
  groups: string[]
  users: string[]
  tags: string[]
  retailGroups: Record<string, IConfigGroup>
  retailTags: ITag[]
}
const calculateContextIdsOfStatuses = ({
  is_individual_results,
  groups,
  users,
  tags,
  retailGroups,
  retailTags,
}: CalculateContextIdsOfStatusesArgs): Record<string, string> => {
  const contextsIds: Record<string, string> = {}

  // Add users to contextsIds
  users.forEach((user) => {
    contextsIds[user] = contextsIds[user] || user
  })

  // Universal function to process groups
  const processGroup = (group: string, context: string) => {
    if (is_individual_results) {
      const groupUsers = extractedUidsFromGroups([group], retailGroups)
      groupUsers.forEach((user) => {
        contextsIds[user] = contextsIds[user] || context
      })
    } else {
      contextsIds[group] = contextsIds[group] || context
    }
  }

  // Process groups
  groups.forEach((group) => processGroup(group, group))

  // Process tags
  tags.forEach((tag) => {
    const tagData = retailTags.find((retailTag) => retailTag.sk === tag)
    if (!tagData) return

    // Process users associated with the tag
    tagData.users?.forEach((user) => {
      contextsIds[user] = contextsIds[user] || tag
    })

    // Process groups associated with the tag
    tagData.groups?.forEach((group) => processGroup(group, tag))
  })

  return contextsIds
}

type TaskInsightsRecipients = {
  groups: string[]
  users: string[]
  tags: string[]
  is_individual_results: boolean
  view?: number
}

export const getTasksInsights =
  ({
    tid,
    recurrence_ts,
    recipients,
  }: {
    tid: string
    recurrence_ts?: string | null
    // When provided, the recipient drill-down uses these directly (e.g. an owner task
    // that isn't present in `insights`). Otherwise it falls back to `insights[tid]`.
    recipients?: TaskInsightsRecipients
  }): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { insights } = getState().tasks_v2
    const {
      config: { groups: groupsObject, tags: retailTags, perm_groups_by_ft },
    } = getState().config
    const { groups: permittedGroups, users: permittedUsers } = getState().config.userWriteTaskPermissions

    try {
      const recipientsSource = recipients ?? insights[tid]
      if (!recipientsSource) return false

      const { groups, users, tags, is_individual_results, view } = recipientsSource
      const isVM = view === keys.DEFAULT_TASK_VIEWS.VM
      const permittedGroupIds = Object.keys(permittedGroups)

      const taskContextsIds = calculateContextIdsOfStatuses({
        is_individual_results,
        groups,
        users,
        tags,
        retailGroups: groupsObject,
        retailTags,
      })

      const { groups: groupsToPayload, users: usersToPayload } = getTaskAudience({
        retailTags,
        groups,
        users,
        tags,
        is_individual_results,
        groupsObject,
        isVM,
        permittedGroupIds,
        permittedUserIds: permittedUsers,
        permittedGroupsByFeature: perm_groups_by_ft,
      })

      const body = {
        tid,
        groups: groupsToPayload,
        users: usersToPayload,
        recurrence_ts: recurrence_ts ?? null,
      }

      const response = await httpAuth.post(endpoints.getTaskInsights, body)

      const transformedData = transformResponseWithCounts(response.data.insights)

      // Combine statuses and contextsIds
      const combinedStatuses = Object.entries(response.data.statuses).reduce((acc, [key, status]) => {
        const taskContextId = taskContextsIds[key]

        // Check if the status is a number before adding to the result
        if (taskContextId && typeof status === 'number') {
          acc[key] = { status, taskContextId }
        }

        return acc
      }, {} as Record<string, { status: number; taskContextId: string }>)

      const deepDiveInsights = {
        contexts: combinedStatuses,
        insights: transformedData,
        tid,
      }
      dispatch(setTasksDeepDiveInsights(deepDiveInsights))

      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }

//a temporary function to filter out type 1 tasks from the data
type ItemWithType = {
  type: number
  [key: string]: any
}

export function filterOutTypeOne<T extends Record<string, ItemWithType>>(
  data: T
): Omit<
  T,
  keyof {
    [K in keyof T as T[K]['type'] extends 1 ? K : never]: any
  }
> {
  return Object.fromEntries(Object.entries(data).filter(([, value]) => value.type !== 1)) as any
}

type GetTasksInsightsParams = {
  from_ts?: string
  to_ts?: string
}

export const getInsightsV2 =
  ({ from_ts, to_ts }: GetTasksInsightsParams): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const activeGroupID = getState().config.activeGroupID
      dispatch(setInsightsLoading(true))
      const body = {
        from_ts,
        to_ts,
        context_id: activeGroupID,
        utc_offset: getUTCOffset(),
      }
      const response = await httpAuth.get(endpoints.getInsightsV2, {
        params: body,
        // server returns the response gzipped (Content-Encoding: gzip) only for this Accept type - SMS-1404
        headers: { Accept: 'application/vnd.storee.gzip+json' },
      })
      const { data } = response

      const tasksInsightsMap = getTasksAudienceMap(filterOutTypeOne(data))
      dispatch(setTaskAudienceMap(tasksInsightsMap))

      dispatch(setTasksInsights(filterOutTypeOne(data), from_ts && to_ts ? { fromTs: from_ts, toTs: to_ts } : null))
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setInsightsLoading(false))
    }
  }

type GetTaskParams = {
  shouldApply?: () => boolean
  tid: string
  task_context_id: string
  result_context_id?: string
  recurrence_ts: string | null
}

type ReturnType = {
  task: Task | null
  subtasks: SubtaskV2[]
}
export const getTask =
  ({
    tid,
    task_context_id,
    result_context_id,
    recurrence_ts,
    shouldApply,
  }: GetTaskParams): ThunkAction<Promise<ReturnType>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const atRequestStart = getState().tasks_v2.allResults[tid] || []
      const query = {
        tid,
        task_context_id,
        result_context_id,
        recurrence_ts,
      }
      const response = await httpAuth.get(endpoints.getTaskV2, { params: query })

      if (shouldApply && !shouldApply()) return { task: null, subtasks: [] }
      const { allResults, subtasks } = getState().tasks_v2
      const { data } = response

      const results: Result[] = data.results?.[tid] ?? []

      const allResultsOfCurrentTask: Results = {
        ...allResults,
        [tid]: mergeFetchedTaskResults(allResults[tid] || [], results, atRequestStart, recurrence_ts),
      }

      const task = { ...data.task, recurrence_ts, dashboardContextId: result_context_id }

      dispatch(setFullTaskFromInsights(task))
      dispatch(setSubtasks({ ...subtasks, ...data.sub_tasks }))

      dispatch(setAllResults(allResultsOfCurrentTask))
      return { task: data.task, subtasks: data.sub_tasks?.[tid] || [] }
    } catch (e) {
      console.error(e)
      return { task: null, subtasks: [] }
    }
  }

/** Load only the modal that initiated this request; closing/reopening invalidates the response. */
export const loadTaskOverviewModal =
  (): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => async (dispatch, getState) => {
    const modal = getState().tasks_v2.taskOverviewModal
    if (!modal) return
    const shouldApply = () => getState().tasks_v2.taskOverviewModal === modal
    const response = await dispatch(
      getTask({
        tid: modal.tid,
        task_context_id: modal.task_context_id,
        result_context_id: modal.result_context_id,
        recurrence_ts: modal.recurrence_ts,
        shouldApply,
      })
    )
    if (shouldApply())
      dispatch(
        patchTaskOverviewModal({
          isLoading: false,
          error: response.task ? null : 'Unable to load task',
        })
      )
  }

const transformData = (data: SubtaskV2[]): Subtasks => {
  return data.reduce((acc, item) => {
    if (!acc[item.tid]) {
      acc[item.tid] = []
    }
    acc[item.tid].push(item)
    return acc
  }, {} as Subtasks)
}

export const getTaskData =
  ({
    tid,
    shouldApply,
  }: {
    tid: string
    shouldApply?: () => boolean
  }): ThunkAction<Promise<ReturnType | null>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const query = {
        tid,
      }
      const response = await httpAuth.get(endpoints.getTaskData, { params: query })
      if (shouldApply && !shouldApply()) return null

      const { subtasks } = getState().tasks_v2
      const { data } = response
      const transformedSubtasks = transformData(data.sub_tasks)

      dispatch(setFullTaskFromInsights(data.task_data))
      dispatch(setSubtasks({ ...subtasks, ...transformedSubtasks }))
      return { task: data.task_data, subtasks: data.sub_tasks }
    } catch (e) {
      console.error(e)
      return null
    }
  }

export const editTask =
  (body: EditTaskBody): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { tasks, insights } = getState().tasks_v2
    dispatch(setIsLoading(true))
    dispatch(clearError())
    try {
      const task = tasks.find((task) => task.tid === body.tid)
      const taskInsights = insights[body.tid]

      if (task) {
        task.deadline = body.updated_deadline
        task.to_ts = body.updated_to_ts
        task.users = body.updated_task_users
        task.groups = body.updated_task_groups
        task.tags = body.updated_task_tags
        task.view = body.task_data.view
        task.title = body.task_data.title

        if (body.task_data.contents && body.task_data.contents.length > 0) {
          task.contents = body.task_data.contents
        }
        const updatedTasks = tasks.map((prevTask) => (prevTask.tid === body.tid ? { ...prevTask, ...task } : prevTask))
        dispatch(setTasks(updatedTasks))
      }

      if (taskInsights) {
        taskInsights.deadline = body.updated_deadline
        taskInsights.to_ts = body.updated_to_ts
        taskInsights.groups = body.updated_task_groups
        taskInsights.users = body.updated_task_users
        taskInsights.tags = body.updated_task_tags
        taskInsights.title = body.task_data.title
        taskInsights.view = body.task_data.view
        taskInsights.priority = body.task_data.priority

        const updatedInsights = {
          ...insights,
          [body.tid]: taskInsights,
        }
        dispatch(setTasksInsights(updatedInsights))
      }
      const response = await httpAuth.put(endpoints.tasksV2, { ...body })
      return response.status === 200
    } catch (e: any) {
      console.error(e)
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to update task'
      const errorStatus = e?.response?.status
      dispatch(setError({ message: errorMessage, status: errorStatus }))
      return false
    } finally {
      dispatch(setIsLoading(false))
    }
  }

type GetResultsOfRecurringTaskInsightsArgs = {
  tid: string
  context_id: string
  recurrence_ts: string[]
}
export const getResultsOfRecurringTaskInsights =
  ({
    tid,
    context_id,
    recurrence_ts,
  }: GetResultsOfRecurringTaskInsightsArgs): ThunkAction<
    Promise<TaskInsightsResult[] | null>,
    RootState,
    unknown,
    AnyAction
  > =>
  async (dispatch) => {
    try {
      const query = {
        tid,
        context_id,
        recurrence_ts: recurrence_ts.join(','),
      }
      const result = await httpAuth.get(endpoints.getResultsOfRecurringTaskInsights, { params: query })
      const { data } = result
      if (data) {
        return data
      }
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export interface IUpdateResultStatus {
  optimistic?: boolean
  payload: IUpdateResultStatusArgs
  type: number
}

export type IUpdateResultStatusArgs = ResultStatusRequest

export const updateResultStatus =
  ({
    payload,
    type,
    optimistic = true,
  }: IUpdateResultStatus): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { tasks, taskFromInsights } = getState().tasks_v2
      const { uid } = getState().auth
      const { tid, result_context_id, st_id, status, rating, rework_count, reservations } = payload
      const { allResults } = getState().tasks_v2
      const currentTask =
        tasks.find((task) => task.tid === tid) ?? (taskFromInsights?.tid === tid ? taskFromInsights : undefined)
      const currentSubtask = getState().tasks_v2.subtasks[tid]?.find((subtask) => subtask.st_id === st_id)

      // find existing result to save all its data
      const occurrence = resolveResultOccurrence(payload.recurrence_ts, currentTask?.recurrence_ts)
      const userResultOfCurrentSubtask = findResultForMutation(allResults?.[tid], st_id!, result_context_id, occurrence)

      const contextIdRecurrenceTs = `${result_context_id}_${occurrence}`

      // if result exists, take all its data, otherwise create a new one
      const existingResult = userResultOfCurrentSubtask || {
        sub_task_type: type,
        st_id: st_id!,
        status: 0,
        context_id: result_context_id,
        images: [],
        videos: [],
        files: [],
        poll_selections: [],
        answer: null,
        is_numeric: false,
        is_multi_choice: false,
        rating: undefined,
      }

      const applyReview = () => {
        const latest = getState().tasks_v2
        // A fetch or media save may have refreshed this result while review was pending.
        // Reconcile only review-owned fields against the same resolved occurrence.
        const latestResult =
          findResultForMutation(latest.allResults?.[tid], st_id!, result_context_id, occurrence) ||
          findResultForMutation(latest.userResults?.[tid], st_id!, result_context_id, occurrence) ||
          existingResult
        // save all existing data and update only status and rating
        const updatedCurrentResult: Result = {
          ...latestResult,
          sub_task_type: type,
          st_id: st_id!,
          status: status, // update status from payload
          context_id: result_context_id,
          context_id_recurrence_ts: contextIdRecurrenceTs,
          ...(type === keys.SUBTASK_TYPES.POLL_SUBTASK || type === keys.SUBTASK_TYPES.PRIVATE_POLL
            ? { is_multi_choice: currentSubtask?.is_multi_choice ?? latestResult.is_multi_choice ?? false }
            : {}),
          updated_by: uid,
          updated_at_ts: Math.floor(Date.now() / 1000),
        }

        // update rating only if it is valid (1-5)
        // if rating === 0 or undefined, do not include it in the object (remove from existing)
        if (rating !== undefined && rating >= keys.RATING_VALUES.POOR && rating <= keys.RATING_VALUES.EXCELLENT) {
          updatedCurrentResult.rating = rating
        } else if (rating === 0 || rating === undefined) {
          // remove rating from the object, if it was set to 0 or undefined
          delete updatedCurrentResult.rating
        }

        // update rework_count if provided
        if (rework_count !== undefined) {
          updatedCurrentResult.rework_count = rework_count
        }

        // update reservations if provided
        if (reservations !== undefined) {
          updatedCurrentResult.reservations = reservations
        }

        dispatch(
          setUserResults({
            ...latest.userResults,
            [tid]: updateResultsArray(latest.userResults?.[tid] || [], updatedCurrentResult),
          })
        )
        dispatch(
          setAllResults({
            ...latest.allResults,
            [tid]: updateResultsArray(latest.allResults?.[tid] || [], updatedCurrentResult),
          })
        )

        // Optimistically mirror into insights so dashboard cards (which read insights, not allResults)
        // reflect the auditor's approve/reject/approve-with-reservation immediately.
        dispatch(
          patchInsightsResult(tid, {
            context_id: result_context_id,
            recurrence_ts: occurrence,
            st_id: st_id!,
            status,
            rating: updatedCurrentResult.rating,
            rework_count: updatedCurrentResult.rework_count,
            created_at_ts: updatedCurrentResult.updated_at_ts,
            score_deduction: reservations?.score_deduction ?? null,
            reservations,
          })
        )
        // Without this the row keeps its old status: the subtask the auditor just reviewed already has a
        // deep-dive row, and that row overwrites the insights patch above in `buildContextRows`.
        dispatch(patchDeepDiveResult(tid, result_context_id, { st_id: st_id!, status }))
      }
      if (optimistic) applyReview()
      // PUT review omits recurrence for nonrecurring results; all local projections use null.
      const reviewPayload = { ...payload }
      if (occurrence === null) delete reviewPayload.recurrence_ts
      else reviewPayload.recurrence_ts = occurrence
      const ok = await submitResultReview(reviewPayload)
      if (ok && !optimistic) applyReview()
      return ok
    } catch (e) {
      console.error(e)
      // in case of error, you can roll back the changes, but for simplicity we leave it as is
      return false
    }
  }

/**
 * Creates a mapping of users, groups, and tags to their associated task IDs
 * @param tasksInsights - Object containing task data with users, groups, and tags
 * @returns Object with users, groups, and tags mappings, where each value is an array of unique task IDs
 */

export interface TasksAudienceMap {
  users: Record<string, string[]>
  groups: Record<string, string[]>
  tags: Record<string, string[]>
}
export const getTaskSummary =
  (tid: string): ThunkAction<Promise<Record<string, any> | null>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      const { data } = await httpAuth.get(endpoints.taskSummary, { params: { tid } })
      return data ?? null
    } catch (e) {
      console.error(e)
      return null
    }
  }

export const getTasksAudienceMap = (tasksInsights: TasksInsights): TasksAudienceMap => {
  // Initialize empty records with Sets to store unique task IDs
  const users: Record<string, Set<string>> = Object.create(null)
  const groups: Record<string, Set<string>> = Object.create(null)
  const tags: Record<string, Set<string>> = Object.create(null)

  // Iterate through each task
  for (const taskId in tasksInsights) {
    // Destructure and rename to avoid naming conflicts
    const { users: taskUsers, groups: taskGroups, tags: taskTags } = tasksInsights[taskId]

    // Process users for current task
    for (const userId of taskUsers) {
      if (!users[userId]) {
        users[userId] = new Set()
      }
      users[userId].add(taskId)
    }

    // Process groups for current task
    for (const groupId of taskGroups) {
      if (!groups[groupId]) {
        groups[groupId] = new Set()
      }
      groups[groupId].add(taskId)
    }

    // Process tags for current task
    for (const tagId of taskTags) {
      if (!tags[tagId]) {
        tags[tagId] = new Set()
      }
      tags[tagId].add(taskId)
    }
  }

  // Convert Sets to arrays and create final object structure
  return {
    users: Object.fromEntries(Object.entries(users).map(([k, v]) => [k, Array.from(v)])),
    groups: Object.fromEntries(Object.entries(groups).map(([k, v]) => [k, Array.from(v)])),
    tags: Object.fromEntries(Object.entries(tags).map(([k, v]) => [k, Array.from(v)])),
  }
}
